import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { HomeHeader } from '../home/HomeHeader'
import { useAuth } from '../../contexts/AuthContext'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Label } from '../ui/label'
import { Input } from '../ui/input'
import { ChevronLeft, Save, Loader2, Eye, EyeOff, Key } from 'lucide-react'
import { apiClient } from '../../lib/api'

interface Model {
  id: string
  name: string
  model: string
}

interface Provider {
  name: string
  models: Model[]
}

export function AdminSettings() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [defaultModel, setDefaultModel] = useState('gemini-2.5-flash')
  const [providers, setProviders] = useState<Record<string, Provider>>({})
  const [apiKeys, setApiKeys] = useState<Record<string, string>>({})
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({})
  const [configuredProviders, setConfiguredProviders] = useState<Set<string>>(new Set())

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      const [settingsResponse, providersResponse] = await Promise.all([
        apiClient.getAdminSettings(),
        apiClient.getAvailableProviders()
      ])
      
      if (settingsResponse.success && settingsResponse.settings) {
        const settings = settingsResponse.settings
        setDefaultModel(settings.default_translation_model || 'gemini-2.5-flash')
        
        // Track which providers have keys configured (without showing encrypted values)
        const configured = new Set<string>()
        if (settings.app_api_key_google) configured.add('google')
        if (settings.app_api_key_openai) configured.add('openai')
        if (settings.app_api_key_anthropic) configured.add('anthropic')
        if (settings.app_api_key_xai) configured.add('xai')
        setConfiguredProviders(configured)
        
        // Don't load encrypted API keys - they're encrypted and can't be displayed
        // Users need to enter new keys if they want to update them
        const keys: Record<string, string> = {}
        setApiKeys(keys)
      }
      
      if (providersResponse.success && providersResponse.providers) {
        setProviders(providersResponse.providers)
      }
    } catch (error) {
      console.error('Failed to load settings:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      
      // Save default model
      await apiClient.updateAdminSetting('default_translation_model', defaultModel)
      
      // Save API keys for each provider (only if a new key was entered)
      const newConfigured = new Set(configuredProviders)
      for (const [provider, key] of Object.entries(apiKeys)) {
        if (key && key.trim()) {
          await apiClient.updateAdminSetting(`app_api_key_${provider}`, key.trim())
          newConfigured.add(provider)
        }
      }
      setConfiguredProviders(newConfigured)
      
      // Clear entered keys after saving
      setApiKeys({})
      
      alert('Settings saved successfully!')
    } catch (error) {
      console.error('Failed to save settings:', error)
      alert('Failed to save settings. Please try again.')
    } finally {
      setSaving(false)
    }
  }
  
  const getModelInfo = (modelId: string) => {
    for (const provider of Object.values(providers)) {
      const model = provider.models.find(m => m.id === modelId)
      if (model) {
        return { model, provider: provider.name }
      }
    }
    return null
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <HomeHeader user={user} onLogout={handleLogout} />
        <div className="container mx-auto px-4 py-16 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground mt-4">Loading settings...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <HomeHeader user={user} onLogout={handleLogout} />
      
      <div className="container mx-auto px-4 py-6 max-w-3xl">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center text-muted-foreground hover:text-foreground mb-4"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Dashboard
          </button>

          <div className="flex items-center gap-3">
            <div className="w-2 h-8 bg-primary rounded-full"></div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Admin Settings</h1>
              <p className="text-sm text-muted-foreground">
                Configure system-wide settings
              </p>
            </div>
          </div>
        </div>

        {/* Settings Form */}
        <Card>
          <CardHeader>
            <CardTitle>Translation Settings</CardTitle>
            <CardDescription>
              Configure system-wide translation settings and API keys
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Default Model */}
            <div className="space-y-2">
              <Label htmlFor="default-model">
                Default Translation Model
              </Label>
              <select
                id="default-model"
                value={defaultModel}
                onChange={(e) => setDefaultModel(e.target.value)}
                className="select-input w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {Object.entries(providers).map(([providerId, provider]) => (
                  <optgroup key={providerId} label={provider.name}>
                    {provider.models.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                This model will be used for all translation jobs. Make sure to configure the API key for its provider below.
              </p>
            </div>

            {/* Model Information */}
            {getModelInfo(defaultModel) && (
              <div className="border border-border rounded-lg p-4 bg-muted/30">
                <h3 className="font-medium text-sm text-foreground mb-2">Selected Model Info</h3>
                <div className="text-xs text-muted-foreground space-y-1">
                  <p><strong>Provider:</strong> {getModelInfo(defaultModel)?.provider}</p>
                  <p><strong>Model:</strong> {getModelInfo(defaultModel)?.model.model}</p>
                  <p><strong>Name:</strong> {getModelInfo(defaultModel)?.model.name}</p>
                </div>
              </div>
            )}

            <div className="border-t border-border pt-6">
              <h3 className="font-medium text-sm text-foreground mb-4">Provider API Keys</h3>
              <p className="text-xs text-muted-foreground mb-4">
                Configure API keys for each provider. These keys will be used for all translation jobs.
              </p>
              
              <div className="space-y-4">
                {Object.entries(providers).map(([providerId, provider]) => {
                  const isConfigured = configuredProviders.has(providerId)
                  return (
                    <div key={providerId} className="space-y-2">
                      <Label htmlFor={`api-key-${providerId}`} className="flex items-center gap-2">
                        <Key className="h-4 w-4" />
                        {provider.name} API Key
                        {isConfigured && (
                          <span className="text-xs text-green-600 dark:text-green-400 ml-2">
                            (Configured)
                          </span>
                        )}
                      </Label>
                      <div className="relative">
                        <Input
                          id={`api-key-${providerId}`}
                          type={showKeys[providerId] ? 'text' : 'password'}
                          value={apiKeys[providerId] || ''}
                          onChange={(e) => setApiKeys({ ...apiKeys, [providerId]: e.target.value })}
                          placeholder={isConfigured ? `Enter new ${provider.name} API key to update` : `Enter ${provider.name} API key`}
                          className="api-key-input pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowKeys({ ...showKeys, [providerId]: !showKeys[providerId] })}
                          className="show-key-btn absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded"
                        >
                          {showKeys[providerId] ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                        </button>
                      </div>
                      {isConfigured && !apiKeys[providerId] && (
                        <p className="text-xs text-muted-foreground">
                          A key is already configured. Enter a new key above to update it.
                        </p>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-border">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Settings
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

